import { CircularProgress, Grid, Pagination, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { PageSizeSelector } from '../../common/pagination/PageSizeSelector';
import { PagedSearch } from '../../common/pagination/paginationTypes';
import { RootState } from '../../store';
import ActivityLogTable, { ActivityLogFilter } from './ActivityLogTable';
import { getActivityLogPagination, getIsActivityLogsLoading } from './redux/activityLogSelectors';
import { fetchActivityLogs } from './redux/activityLogSlices';

const mapStateToProps = (state: RootState) => ({
    pagination: getActivityLogPagination(state),
    isLoading: getIsActivityLogsLoading(state)
});
type Props = ReturnType<typeof mapStateToProps>;

const ActivityLog = ({ pagination, isLoading }: Props) => {
    const dispatch = useDispatch();
    const [search, setSearch] = useState<PagedSearch>({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        searchString: pagination.searchString,
        sortColumn: pagination.sortColumn,
        sortOrder: pagination.sortOrder
    });

    const pages = useMemo(() => 1 + Math.floor(pagination.totalCount / pagination.pageSize), [pagination]);

    const handleOnSort = (sortColumn: string, sortOrder: 'asc' | 'desc') => {
        setSearch(s => ({
            ...s,
            sortColumn: sortColumn,
            sortOrder: sortOrder,
            pageIndex: 0
        }));
    };

    const handleSetPageSize = (pageSize: 25 | 50 | 100 | 500) => {
        setSearch(s => ({
            ...s,
            pageSize,
            pageIndex: Math.floor((pagination.pageSize * pagination.pageIndex) / pageSize)
        }));
    };

    const handleSetPageIndex = (pageIndex: number) => {
        setSearch(s => ({
            ...s,
            pageIndex
        }));
    };

    const handleFiltersChanged = (filter: ActivityLogFilter) => {
        setSearch(s => ({
            ...s,
            searchString: `${filter.id}||${filter.type}||${filter.action}||${filter.createdBy}||${filter.createdDate}`
        }));
    };

    useEffect(() => {
        dispatch(fetchActivityLogs.request(search));
    }, [search, dispatch]);

    return (
        <Grid container spacing={1} direction="column">
            <Grid item>
                <Grid container direction="row" justifyContent="flex-end">
                    <Grid item xs={2}>
                        <Grid container direction="row" spacing={2} justifyContent="flex-end" wrap="nowrap">
                            <Grid item>
                                <CircularProgress sx={{ visibility: !isLoading ? 'hidden' : 'initial' }} />
                            </Grid>

                            <Grid item>
                                <PageSizeSelector
                                    selectedPageSize={search.pageSize}
                                    setSelectedPageSize={handleSetPageSize}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <ActivityLogTable onSort={handleOnSort} onFilterChanged={handleFiltersChanged} />
            </Grid>
            <Grid item>
                <Grid container direction="row" justifyContent="space-between">
                    <Grid item>
                        <Typography variant="subtitle2">
                            {`Total: ${pagination.totalCount} rows ${
                                pagination.searchString != null &&
                                pagination.searchString.replaceAll('|', '').length > 0
                                    ? `(${pagination.totalUnfilteredCount} unfiltered)`
                                    : ''
                            }`}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Pagination
                            count={pages}
                            page={search.pageIndex + 1}
                            onChange={(_, p) => handleSetPageIndex(p - 1)}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default connect(mapStateToProps)(ActivityLog);
